import React, { useEffect, useState } from 'react';
import {
  Steps, Form, Input, Select, Button, Card, Row, Col, DatePicker, InputNumber,
  Breadcrumb, Grid, message, Divider, Descriptions, Space, AutoComplete,
  Typography, Avatar, Tag, Statistic, Modal, Tooltip, Popconfirm,
} from 'antd';
import {
  RightOutlined, UserOutlined, FileProtectOutlined, DollarOutlined,
  CheckCircleOutlined, CheckOutlined, SaveOutlined, CarOutlined, CalendarOutlined,
  ArrowLeftOutlined, ArrowRightOutlined, SearchOutlined, PlusOutlined,
  PhoneOutlined, IdcardOutlined, BankOutlined, EditOutlined, CloseOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { contratsService, clientsService, compagniesService, typesAssuranceService, agencesService, settingsService, marquesVehiculeService, referentielsVehiculeService } from '../../common/services';
import { useCanSwitchAgence, useUserAgenceId } from '../../common/hooks';
import { toast } from 'react-toastify';
import { formatPhone } from '../../common/utils/format';
import TermHelp from '../../components/termHelp';

import './nouveau-contrat.page.scss';

const { Option } = Select;
const { Title, Text } = Typography;

const moment = require('moment');
// antd v5 utilise dayjs : le DatePicker interne attend du dayjs, alors que le
// formulaire travaille en moment. On convertit moment<->dayjs uniquement pour le picker.
const dayjs = require('dayjs');

// Champs montants en FCFA : ENTIERS uniquement. Un point ou une espace saisis
// sont traités comme séparateurs de milliers (jamais comme décimale) :
// « 14.697 » ou « 14 697 » -> 14697. Empêche l'erreur « 14.697 = 14,697 » qui
// faussait la prime TTC.
const fcfaFormatter = (v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const fcfaParser = (v) => (v == null ? '' : String(v).replace(/[^\d]/g, ''));
// Props communes à appliquer sur tous les champs InputNumber de montant FCFA.
const FCFA_INPUT_PROPS = {
  min: 0,
  precision: 0,
  style: { width: '100%', borderRadius: 8 },
  formatter: fcfaFormatter,
  parser: fcfaParser,
};

// DatePicker avec masque auto DD/MM/YYYY
const MaskedDatePicker = ({ value, onChange, style, placeholder, ...rest }) => {
  const [textVal, setTextVal] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (value && value.format) setTextVal(value.format('DD/MM/YYYY'));
  }, [value]);

  const applyMask = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    let masked = '';
    if (digits.length > 0) masked += digits.slice(0, 2);
    if (digits.length > 2) masked += '/' + digits.slice(2, 4);
    if (digits.length > 4) masked += '/' + digits.slice(4, 8);
    return masked;
  };

  const handleTextChange = (e) => {
    const masked = applyMask(e.target.value);
    setTextVal(masked);
    const digits = masked.replace(/\D/g, '');
    if (digits.length === 8) {
      const date = moment(masked, 'DD/MM/YYYY', true);
      if (date.isValid()) onChange?.(date);
    }
  };

  const handlePickerChange = (date) => {
    // date vient du picker antd v5 = objet dayjs -> on reconvertit en moment pour le formulaire
    const asMoment = date ? moment(date.valueOf()) : null;
    onChange?.(asMoment);
    if (asMoment) setTextVal(asMoment.format('DD/MM/YYYY'));
    else setTextVal('');
    setPickerOpen(false);
  };

  return (
    <Space.Compact style={{ width: '100%', ...style }}>
      <Input
        value={textVal}
        onChange={handleTextChange}
        placeholder={placeholder || 'JJ/MM/AAAA'}
        maxLength={10}
        style={{ flex: 1, borderRadius: '8px 0 0 8px' }}
      />
      <Button
        icon={<CalendarOutlined />}
        onClick={() => setPickerOpen(true)}
        style={{ borderRadius: '0 8px 8px 0' }}
      />
      <DatePicker
        value={value ? dayjs(value.valueOf()) : null}
        onChange={handlePickerChange}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        format="DD/MM/YYYY"
        style={{ width: 0, height: 0, overflow: 'hidden', position: 'absolute', opacity: 0 }}
        allowClear={false}
        inputReadOnly
        {...rest}
      />
    </Space.Compact>
  );
};

const NouveauContrat = () => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [compagnies, setCompagnies] = useState([]);
  const [typesAssurance, setTypesAssurance] = useState([]);
  const [agences, setAgences] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [formData, setFormData] = useState({});
  const [settings, setSettings] = useState({ taxe_taux: 12, fga_taux: 2, cedeao_montant_tourisme: 3000, cedeao_montant_marchandise: 5000 });
  const [marquesData, setMarquesData] = useState([]);
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const renewId = searchParams.get('renew');
  const isEditMode = !!editId;
  const isRenewMode = !!renewId;
  const [editLoading, setEditLoading] = useState(false);
  const [refEnergies, setRefEnergies] = useState([]);
  const [refCategoriesVehicule, setRefCategoriesVehicule] = useState([]);
  const [refUsagesVehicule, setRefUsagesVehicule] = useState([]);
  const [refClassesConducteur, setRefClassesConducteur] = useState([]);
  const [guideOpen, setGuideOpen] = useState(false);
  const navigate = useNavigate();
  const canSwitchAgence = useCanSwitchAgence();
  const userAgenceId = useUserAgenceId();

  // Rôle restreint : agence verrouillée sur la sienne (super admin : libre)
  useEffect(() => {
    if (!canSwitchAgence && userAgenceId && !isEditMode && !isRenewMode) {
      form.setFieldsValue({ agence: userAgenceId });
    }
  }, [canSwitchAgence, userAgenceId, isEditMode, isRenewMode, form]);

  const marquesOptions = marquesData.flatMap(m => {
    const opts = [{ value: m.nom }];
    (m.modeles || []).forEach(mod => opts.push({ value: `${m.nom} ${mod}` }));
    return opts;
  });
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  useEffect(() => {
    const fetchRefs = async () => {
      try {
        const [compRes, typeRes, agRes, settRes, marqRes, refRes] = await Promise.all([
          compagniesService.getAll().catch(() => []),
          typesAssuranceService.getAll().catch(() => []),
          agencesService.getAll().catch(() => []),
          settingsService.getAll().catch(() => []),
          marquesVehiculeService.getAll().catch(() => []),
          referentielsVehiculeService.getAll().catch(() => []),
        ]);
        setCompagnies(Array.isArray(compRes) ? compRes : compRes?.data || []);
        setTypesAssurance(Array.isArray(typeRes) ? typeRes : typeRes?.data || []);
        setAgences(Array.isArray(agRes) ? agRes : agRes?.data || []);
        setMarquesData(Array.isArray(marqRes) ? marqRes : marqRes?.data || []);
        const allRefs = Array.isArray(refRes) ? refRes : refRes?.data || [];
        const sortByNom = (arr) => [...arr].sort((a, b) =>
          (a.nom || '').localeCompare(b.nom || '', undefined, { numeric: true, sensitivity: 'base' })
        );
        setRefEnergies(sortByNom(allRefs.filter(r => r.type === 'energie')));
        setRefCategoriesVehicule(sortByNom(allRefs.filter(r => r.type === 'categorie_vehicule')));
        setRefUsagesVehicule(sortByNom(allRefs.filter(r => r.type === 'usage_vehicule')));
        setRefClassesConducteur(sortByNom(allRefs.filter(r => r.type === 'classe_conducteur')));
        const settingsArr = Array.isArray(settRes) ? settRes : settRes?.data || [];
        if (settingsArr.length > 0) {
          const map = {};
          settingsArr.forEach(s => { if (s.key && s.value !== undefined) map[s.key] = s.value; });
          setSettings(prev => ({ ...prev, ...map }));
        }
      } catch (e) { console.error(e); }
    };
    fetchRefs();
  }, []);

  useEffect(() => {
    if (isEditMode || isRenewMode) return;
    const duree = form.getFieldValue('duree') || 12;
    const effet = moment();
    form.setFieldsValue({
      dateEffet: effet,
      dateEcheance: effet.clone().add(duree, 'months').subtract(1, 'day'),
      dateProduction: moment(),
    });
  }, [isEditMode, isRenewMode, form]);

  useEffect(() => {
    const sourceId = editId || renewId;
    if (!sourceId) return;
    const loadContrat = async () => {
      try {
        setEditLoading(true);
        const res = await contratsService.getById(sourceId);
        const contrat = res?.data || res;
        if (!contrat) return;
        if (contrat.client) setSelectedClient(contrat.client);
        if (contrat.typeAssurance) setSelectedType(contrat.typeAssurance);
        const duree = contrat.duree || 12;
        // Renouvellement : nouveau dateEffet = ancienne dateEcheance + 1 jour, échéance recalculée
        const renewEffet = isRenewMode && contrat.dateEcheance
          ? moment(contrat.dateEcheance).add(1, 'day')
          : null;
        const fields = {
          compagnie: contrat.compagnie?._id || contrat.compagnie,
          typeAssurance: contrat.typeAssurance?._id || contrat.typeAssurance,
          agence: contrat.agence?._id || contrat.agence,
          // Renouvellement : on garde par defaut le meme numero de police (regle metier).
          // Modifiable si le contrat est renouvele sur une autre compagnie.
          numeroPolice: contrat.numeroPolice,
          numeroAttestation: isRenewMode ? '' : contrat.numeroAttestation,
          duree,
          dateEffet: isRenewMode
            ? renewEffet
            : (contrat.dateEffet ? moment(contrat.dateEffet) : null),
          dateEcheance: isRenewMode
            ? (renewEffet ? renewEffet.clone().add(duree, 'months').subtract(1, 'day') : null)
            : (contrat.dateEcheance ? moment(contrat.dateEcheance) : null),
          dateProduction: isRenewMode
            ? moment()
            : (contrat.dateProduction ? moment(contrat.dateProduction) : moment()),
          immatriculation: contrat.immatriculation,
          marqueVehicule: contrat.marqueVehicule,
          numeroCedeao: isRenewMode ? '' : contrat.numeroCedeao,
          energie: contrat.energie,
          categorieVehicule: contrat.categorieVehicule,
          dateMiseCirculation: contrat.dateMiseCirculation ? moment(contrat.dateMiseCirculation) : null,
          valeurNeuf: contrat.valeurNeuf,
          usageVehicule: contrat.usageVehicule,
          conducteurHabituel: contrat.conducteurHabituel,
          classeConduteur: contrat.classeConduteur,
          tauxBns: contrat.tauxBns,
          primeNette: contrat.primeNette,
          accessoiresCourtier: contrat.accessoiresCourtier != null
            ? contrat.accessoiresCourtier
            : (contrat.accessoiresAssureur != null ? 0 : (contrat.accessoires || 0)),
          accessoiresAssureur: contrat.accessoiresAssureur || 0,
          tauxCommission: contrat.tauxCommission || 0,
          taxes: contrat.taxes || 0,
          fga: contrat.fga || 0,
          cedeao: contrat.cedeao || 0,
          commission: contrat.commissionCourtier || 0,
          primeTTC: contrat.primeTTC || 0,
        };
        if (contrat.vehicule) fields.vehicule = contrat.vehicule;
        form.setFieldsValue(fields);
        setFormData(fields);
      } catch (e) {
        toast.error('Erreur lors du chargement du contrat');
      } finally {
        setEditLoading(false);
      }
    };
    loadContrat();
  }, [editId, renewId, isRenewMode]);

  useEffect(() => {
    if (clientSearch.length >= 2) {
      clientsService.search(clientSearch)
        .then(res => setClients(Array.isArray(res) ? res : res?.data || []))
        .catch(() => {});
    }
  }, [clientSearch]);

  const isAutoType = () => selectedType?.code?.toUpperCase() === 'AUTO';

  const [taxesManuallyEdited, setTaxesManuallyEdited] = useState(false);
  const [fgaManuallyEdited, setFgaManuallyEdited] = useState(false);

  const calcPrime = (recalcAuto = true) => {
    const values = form.getFieldsValue();
    const primeNette = values.primeNette || 0;
    const accessoiresCourtier = values.accessoiresCourtier || 0;
    const accessoiresAssureur = values.accessoiresAssureur || 0;
    const tauxCommission = values.tauxCommission || 0;
    const globalTaxeTaux = Number(settings.taxe_taux) || 12;
    const taxeTaux = (selectedType?.tauxTaxe != null && selectedType.tauxTaxe >= 0) ? selectedType.tauxTaxe : globalTaxeTaux;
    const fgaTaux = Number(settings.fga_taux) || 2;
    const taxes = (!taxesManuallyEdited && recalcAuto) ? Math.round(primeNette * (taxeTaux / 100)) : (values.taxes || 0);
    const fga = (!fgaManuallyEdited && recalcAuto) ? (isAutoType() ? Math.round(primeNette * (fgaTaux / 100)) : 0) : (values.fga || 0);
    const cedeao = values.cedeao || 0;
    const commission = Math.round(primeNette * (tauxCommission / 100));
    const primeTTC = primeNette + fga + cedeao + taxes + accessoiresCourtier + accessoiresAssureur;
    const fieldsToUpdate = { primeTTC, commission };
    if (!taxesManuallyEdited && recalcAuto) fieldsToUpdate.taxes = taxes;
    if (!fgaManuallyEdited && recalcAuto) fieldsToUpdate.fga = fga;
    form.setFieldsValue(fieldsToUpdate);
  };

  const recalcTTC = () => calcPrime(false);
  const handleTaxesChange = (val) => { setTaxesManuallyEdited(true); form.setFieldsValue({ taxes: val || 0 }); recalcTTC(); };
  const handleFgaChange = (val) => { setFgaManuallyEdited(true); form.setFieldsValue({ fga: val || 0 }); recalcTTC(); };
  const handleCedeaoChange = (val) => { form.setFieldsValue({ cedeao: val || 0 }); recalcTTC(); };

  const FIELD_LABELS = {
    compagnie: 'Compagnie', typeAssurance: "Type d'assurance", agence: 'Agence',
    numeroPolice: 'N° Police', dateEffet: "Date d'effet", dateEcheance: "Date d'échéance", dateProduction: 'Date de production',
    categorieVehicule: 'Catégorie véhicule', primeNette: 'Prime nette',
  };

  const handleNext = async () => {
    try {
      if (currentStep === 0) {
        if (!selectedClient) { toast.warning('Veuillez sélectionner un client avant de continuer'); return; }
      }
      if (currentStep === 1) {
        const requiredFields = ['compagnie', 'typeAssurance', 'agence', 'numeroPolice', 'dateEffet', 'dateEcheance', 'dateProduction'];
        if (isAutoType()) requiredFields.push('categorieVehicule');
        await form.validateFields(requiredFields);
      }
      if (currentStep === 2) {
        await form.validateFields(['primeNette']);
        calcPrime();
      }
      setFormData(prev => ({ ...prev, ...form.getFieldsValue() }));
      setCurrentStep(prev => prev + 1);
      setTimeout(() => {
        document.querySelector('.content-wrapper')?.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    } catch (e) {
      const fields = (e?.errorFields || []).map(f => FIELD_LABELS[f.name?.[0]] || f.name?.[0]).filter(Boolean);
      if (fields.length > 0) {
        toast.error(`Champs requis manquants : ${fields.join(', ')}`, { autoClose: 5000 });
      } else if (e?.message) {
        toast.error(e.message);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const allValues = { ...formData, ...form.getFieldsValue(), client: selectedClient._id };
      const accCourtier = allValues.accessoiresCourtier || 0;
      const accAssureur = allValues.accessoiresAssureur || 0;
      allValues.commissionCourtier = allValues.commission || 0;
      allValues.montantReverseAssureur = ((allValues.primeNette || 0) - (allValues.commission || 0)) + accAssureur;
      allValues.accessoires = accCourtier + accAssureur;
      if (isEditMode) {
        await contratsService.update(editId, allValues);
        toast.success('Contrat modifie avec succès');
      } else {
        // Renouvellement : lier au contrat precedent (tracabilite + tolerance du meme n° de police)
        if (isRenewMode && renewId) allValues.contratPrecedent = renewId;
        await contratsService.create(allValues);
        toast.success(isRenewMode ? 'Contrat renouvelé avec succès' : 'Contrat cree avec succès');
      }
      setTimeout(() => navigate('/dashboard/contrats'), 500);
    } catch (error) {
      const msg = error?.message || error?.data?.message || 'Erreur lors de la sauvegarde du contrat';
      const numPolice = formData?.numeroPolice || form.getFieldValue('numeroPolice') || '';
      if (error?.status === 409 || msg.includes('numéro de police')) {
        const errMsg = `Le numéro de police "${numPolice}" est déjà attribué a un contrat existant. Veuillez saisir un autre numero.`;
        toast.error(errMsg, { autoClose: 8000 });
      } else {
        toast.error(msg, { autoClose: 6000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const stepItems = [
    { title: 'Souscripteur', icon: currentStep > 0 ? <CheckOutlined style={{ color: '#fff' }} /> : <UserOutlined />, status: currentStep > 0 ? 'finish' : currentStep === 0 ? 'process' : 'wait' },
    { title: 'Contrat', icon: currentStep > 1 ? <CheckOutlined style={{ color: '#fff' }} /> : <FileProtectOutlined />, status: currentStep > 1 ? 'finish' : currentStep === 1 ? 'process' : 'wait' },
    { title: 'Prime', icon: currentStep > 2 ? <CheckOutlined style={{ color: '#fff' }} /> : <DollarOutlined />, status: currentStep > 2 ? 'finish' : currentStep === 2 ? 'process' : 'wait' },
    { title: 'Confirmation', icon: <CheckCircleOutlined />, status: currentStep === 3 ? 'process' : 'wait' },
  ];

  const fmt = (v) => (v || 0).toLocaleString('fr-FR');

  return (
    <div className="nouveau-contrat-page">
      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: isMobile ? 12 : 24, paddingRight: isMobile ? 12 : 24 }}>
        {/* Header compact */}
        <div className="nc-header">
          <div className="nc-header-text">
            <h1 className="nc-title">
              <FileProtectOutlined />
              {isEditMode ? 'Modifier le contrat' : (isRenewMode ? 'Renouvellement de contrat' : 'Nouveau contrat')}
            </h1>
            <p className="nc-subtitle">
              {isEditMode
                ? 'Modification des informations du contrat'
                : (isRenewMode
                  ? 'Données reprises du contrat précédent — vérifiez et complétez le N° de police'
                  : "Saisie d'un nouveau contrat d'assurance")}
            </p>
          </div>
          <div className="nc-header-actions">
            <Button
              icon={<QuestionCircleOutlined />}
              className="nc-btn-guide"
              onClick={() => setGuideOpen(true)}
            >
              Guide
            </Button>
            <Popconfirm
              title="Abandonner la saisie ?"
              description="Les informations non enregistrées seront perdues."
              okText="Oui, abandonner"
              cancelText="Continuer"
              okButtonProps={{ danger: true }}
              onConfirm={() => navigate('/dashboard/contrats')}
            >
              <Button icon={<CloseOutlined />} className="nc-btn-cancel">
                Annuler
              </Button>
            </Popconfirm>
          </div>
        </div>

        {/* Guide explicatif */}
        <Modal
          open={guideOpen}
          onCancel={() => setGuideOpen(false)}
          className="nc-guide-modal"
          title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Guide — Créer un contrat</span>}
          closeIcon={<span className="nc-guide-close"><CloseOutlined /> Fermer</span>}
          footer={<Button type="primary" onClick={() => setGuideOpen(false)}>J'ai compris</Button>}
          width={640}
        >
          <Text type="secondary">
            La création d'un contrat se fait en 4 étapes. À chaque étape, suivez l'instruction
            correspondante ci-dessous. Vous pouvez revenir à une étape précédente en cliquant
            dessus dans la barre d'étapes.
          </Text>
          <ol className="nc-guide-list">
            <li>
              <UserOutlined className="nc-guide-ic" />
              <div>
                <strong>Étape 1 · Souscripteur</strong> — Recherchez le client par nom, téléphone
                ou code, puis <strong>cliquez sur le résultat dans la liste</strong> pour le
                sélectionner, et cliquez sur « Suivant ».
                <div className="nc-guide-note">
                  <strong>Important :</strong> si le client n'apparaît pas dans les résultats de
                  recherche, c'est qu'il n'existe pas encore. Cliquez alors sur
                  « Créer un nouveau client », créez-le, puis revenez à cette étape pour le
                  sélectionner.
                </div>
              </div>
            </li>
            <li>
              <FileProtectOutlined className="nc-guide-ic" />
              <div>
                <strong>Étape 2 · Contrat</strong> — Renseignez la compagnie, le type d'assurance,
                le N° de police, les dates d'effet et d'échéance et l'agence. Les champs marqués
                d'un <span style={{ color: '#C8102E' }}>*</span> sont obligatoires.
              </div>
            </li>
            <li>
              <DollarOutlined className="nc-guide-ic" />
              <div>
                <strong>Étape 3 · Prime</strong> — Saisissez la prime nette, les accessoires et
                les taxes. La <strong>prime TTC</strong> et la <strong>commission</strong> se
                calculent automatiquement.
              </div>
            </li>
            <li>
              <CheckCircleOutlined className="nc-guide-ic" />
              <div>
                <strong>Étape 4 · Confirmation</strong> — Vérifiez le récapitulatif, puis cliquez
                sur « Enregistrer le contrat ».
              </div>
            </li>
          </ol>
        </Modal>

        {/* Stepper amélioré */}
        <Card className="nc-stepper-card">
          <Steps
            current={currentStep}
            items={stepItems}
            size={isMobile ? 'small' : 'default'}
            onChange={(target) => { if (target < currentStep) setCurrentStep(target); }}
            className="nc-steps"
            labelPlacement={isMobile ? 'vertical' : 'horizontal'}
          />
        </Card>

        {/* Step Content */}
        <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }} bodyStyle={{ padding: isMobile ? 16 : 32 }}>
          <Form form={form} layout="vertical" size={isMobile ? 'small' : 'middle'}
            onValuesChange={(changed, all) => {
              if (!('duree' in changed) && !('dateEffet' in changed)) return;
              const duree = Number(all.duree);
              if (!duree || duree <= 0) return;
              const effet = all.dateEffet && all.dateEffet.isValid && all.dateEffet.isValid()
                ? all.dateEffet
                : moment();
              const echeance = effet.clone().add(duree, 'months').subtract(1, 'day');
              const patch = { dateEcheance: echeance };
              if (!all.dateEffet) patch.dateEffet = effet;
              form.setFieldsValue(patch);
            }}
          >

            {/* Step 1: Client */}
            {currentStep === 0 && (() => {
              const buildClientOption = (c) => {
                const displayName = c.type === 'entreprise'
                  ? (c.raisonSociale || c.nom || '')
                  : `${c.nom || ''} ${c.prenom || ''}`.trim();
                const initials = displayName.split(' ').filter(Boolean).map(w => w[0]).join('').substring(0, 2).toUpperCase();
                const isEntreprise = c.type === 'entreprise';
                return {
                  value: `${c.code || ''}|${c._id || ''}`,
                  client: c,
                  label: (
                    <div className="nc-option-row">
                      <div className={`nc-option-avatar ${isEntreprise ? 'is-entreprise' : 'is-particulier'}`}>{initials}</div>
                      <div className="nc-option-body">
                        <div className="nc-option-name">{displayName}</div>
                        <div className="nc-option-meta">
                          <span className="nc-option-code">{c.code || '—'}</span>
                          <span className="nc-option-sep">·</span>
                          <span>{formatPhone(c.telephone)}</span>
                          {c.agence?.nom && (<><span className="nc-option-sep">·</span><span>{c.agence.nom}</span></>)}
                        </div>
                      </div>
                      <Tag color={isEntreprise ? 'orange' : 'blue'} className="nc-option-type">
                        {isEntreprise ? 'ENT' : 'PART'}
                      </Tag>
                    </div>
                  ),
                };
              };

              const hasQuery = clientSearch && clientSearch.length >= 2;
              const showNoResults = hasQuery && clients.length === 0;

              return (
                <div className="nc-step-souscripteur">
                  <div className="nc-step-header">
                    <Title level={5} className="nc-step-title">
                      <UserOutlined /> Sélectionner le souscripteur
                    </Title>
                    <Text type="secondary" className="nc-step-hint">
                      Recherchez le client, cliquez sur le résultat dans la liste, puis sur « Suivant ». Étape 1 sur 4.
                    </Text>
                  </div>

                  <div className="nc-search-block">
                    <label htmlFor="nc-client-search" className="nc-search-label">
                      Rechercher un client
                    </label>
                    <div className="nc-search-group">
                      <AutoComplete
                        id="nc-client-search"
                        placeholder="Tapez le nom, téléphone ou code client…"
                        value={clientSearch}
                        onSearch={setClientSearch}
                        onChange={(v) => setClientSearch(v)}
                        onSelect={(val, option) => {
                          setSelectedClient(option.client);
                          setClientSearch('');
                        }}
                        options={clients.map(buildClientOption)}
                        className="nc-search-input"
                        popupClassName="nc-autocomplete-popup"
                        notFoundContent={null}
                        size="large"
                        allowClear={{ clearIcon: <CloseOutlined /> }}
                      />
                      <div className="nc-search-btn" aria-hidden="true">
                        <SearchOutlined />
                      </div>
                    </div>

                    {/* Helper line : min car. OU lien créer */}
                    {!selectedClient && !showNoResults && (
                      <div className="nc-search-helper">
                        <span className="nc-search-helper-text">
                          {hasQuery ? `${clients.length} résultat${clients.length > 1 ? 's' : ''}${clients.length > 0 ? ' — cliquez sur un client puis sur « Suivant »' : ''}` : 'Min. 2 caractères pour rechercher'}
                        </span>
                        <button
                          type="button"
                          className="nc-link-create"
                          onClick={() => navigate('/dashboard/nouveau-client')}
                        >
                          Le client n'existe pas ? <strong>Créer un nouveau client →</strong>
                        </button>
                      </div>
                    )}

                    {/* Aucun résultat — CTA inline */}
                    {showNoResults && !selectedClient && (
                      <div className="nc-no-results">
                        <div className="nc-no-results-icon"><UserOutlined /></div>
                        <div className="nc-no-results-body">
                          <p className="nc-no-results-title">
                            Aucun client trouvé pour <strong>« {clientSearch} »</strong>
                          </p>
                          <p className="nc-no-results-hint">
                            Vérifiez l'orthographe ou créez un nouveau client.
                          </p>
                        </div>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => navigate(`/dashboard/nouveau-client?prefill=${encodeURIComponent(clientSearch)}`)}
                          className="nc-no-results-btn"
                        >
                          Créer ce client
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Souscripteur sélectionné */}
                  {selectedClient && (
                    <>
                      <div className="nc-selected-label">
                        <CheckCircleOutlined /> Souscripteur sélectionné
                      </div>
                      <Card
                        className="nc-selected-card"
                        bodyStyle={{ padding: isMobile ? 16 : 24 }}
                      >
                        <div className="nc-selected-content">
                          <Avatar size={56} className="nc-selected-avatar">
                            {(selectedClient.type === 'entreprise'
                              ? (selectedClient.raisonSociale || selectedClient.nom || '')
                              : `${selectedClient.nom || ''} ${selectedClient.prenom || ''}`
                            ).split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
                          </Avatar>
                          <div className="nc-selected-body">
                            <Text strong className="nc-selected-name">
                              {selectedClient.type === 'entreprise'
                                ? (selectedClient.raisonSociale || selectedClient.nom || '')
                                : `${selectedClient.nom || ''} ${selectedClient.prenom || ''}`.trim()}
                            </Text>
                            <Space size={16} wrap style={{ marginTop: 6 }}>
                              <Tag color={selectedClient.type === 'entreprise' ? 'orange' : 'blue'} className="nc-selected-tag">
                                {selectedClient.type === 'entreprise' ? 'ENTREPRISE' : 'PARTICULIER'}
                              </Tag>
                              <Text type="secondary"><IdcardOutlined style={{ marginRight: 4 }} />{selectedClient.code}</Text>
                              <Text type="secondary"><PhoneOutlined style={{ marginRight: 4 }} />{formatPhone(selectedClient.telephone)}</Text>
                              {selectedClient.agence?.nom && (
                                <Text type="secondary"><BankOutlined style={{ marginRight: 4 }} />{selectedClient.agence.nom}</Text>
                              )}
                            </Space>
                          </div>
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => { setSelectedClient(null); setClientSearch(''); }}
                            className="nc-selected-clear"
                            aria-label="Désélectionner le client"
                          />
                        </div>
                      </Card>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Step 2: Contract details */}
            {currentStep === 1 && (
              <div>
                <Title level={5} style={{ marginBottom: 20 }}>
                  <FileProtectOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />
                  Détails du contrat
                </Title>

                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Form.Item name="compagnie" label="Compagnie" rules={[{ required: true, message: 'Requis' }]}>
                      <Select placeholder="Sélectionner" showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>{compagnies.map(c => <Option key={c._id} value={c._id}>{c.nom}</Option>)}</Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="typeAssurance" label="Type d'assurance" rules={[{ required: true, message: 'Requis' }]}>
                      <Select placeholder="Sélectionner" showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())} onChange={(val) => {
                        const t = typesAssurance.find(ta => ta._id === val);
                        setSelectedType(t);
                        setTaxesManuallyEdited(false);
                        setFgaManuallyEdited(false);
                        if (t?.tauxCommission != null) form.setFieldsValue({ tauxCommission: t.tauxCommission });
                      }}>
                        {typesAssurance.map(t => <Option key={t._id} value={t._id}>{t.nom}</Option>)}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="agence" label="Agence" rules={[{ required: true, message: 'Requis' }]}>
                      <Select placeholder="Sélectionner" disabled={!canSwitchAgence} showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>{agences.map(a => <Option key={a._id} value={a._id}>{a.nom}</Option>)}</Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Form.Item name="numeroPolice" label="N° Police" rules={[{ required: true, message: 'Requis' }]}>
                      <Input placeholder="Numéro de police" style={{ borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="numeroAttestation" label="N. Attestation">
                      <Input placeholder="Numéro d'attestation" style={{ borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="duree" label="Duree (mois)" initialValue={12}>
                      <InputNumber min={1} max={120} style={{ width: '100%', borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Form.Item name="dateEffet" label="Date d'effet" rules={[{ required: true, message: "La date d'effet est obligatoire" }]}>
                      <MaskedDatePicker style={{ width: '100%' }} placeholder="JJ/MM/AAAA" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="dateEcheance"
                      label="Date d'échéance"
                      rules={[
                        { required: true, message: "La date d'échéance est obligatoire" },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const effet = getFieldValue('dateEffet');
                            if (!value || !effet) return Promise.resolve();
                            if (!value.isAfter(effet, 'day')) {
                              return Promise.reject(new Error("La date d'échéance doit être postérieure à la date d'effet"));
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <MaskedDatePicker style={{ width: '100%' }} placeholder="JJ/MM/AAAA" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="dateProduction"
                      label="Date de production"
                      tooltip="Jour réel de la production (par défaut aujourd'hui). À modifier uniquement pour un contrat antidaté saisi a posteriori."
                      rules={[
                        { required: true, message: 'La date de production est obligatoire' },
                        {
                          validator(_, value) {
                            // Comparaison par timestamp (compatible moment ET dayjs) — fin de journée d'aujourd'hui
                            if (value && value.valueOf() > moment().endOf('day').valueOf()) {
                              return Promise.reject(new Error('La date de production ne peut pas être dans le futur'));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <MaskedDatePicker
                        style={{ width: '100%' }}
                        placeholder="JJ/MM/AAAA"
                        disabledDate={(d) => d && d.valueOf() > moment().endOf('day').valueOf()}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {isAutoType() && (
                  <>
                    <Divider orientation="left" style={{ color: '#1A2B5F', fontWeight: 600 }}>
                      <CarOutlined style={{ marginRight: 6 }} /> Informations vehicule
                    </Divider>
                    <Row gutter={16}>
                      <Col xs={24} sm={8}>
                        <Form.Item name="categorieVehicule" label="Catégorie vehicule" rules={[{ required: true, message: 'Requis' }]}>
                          <Select placeholder="Sélectionner" showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                            {refCategoriesVehicule.map(r => <Option key={r._id} value={r.nom}>{r.nom}</Option>)}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item name="immatriculation" label="Immatriculation" normalize={(v) => v ? v.toUpperCase() : v}>
                          <Input placeholder="Ex: 1234 AB 01" prefix={<CarOutlined />} style={{ borderRadius: 8, textTransform: 'uppercase' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item name="marqueVehicule" label="Marque / Modele">
                          <AutoComplete placeholder="Tapez ou sélectionnez..." options={marquesOptions}
                            filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={8}>
                        <Form.Item name="numeroCedeao" label="N. CEDEAO" normalize={(v) => v ? v.toUpperCase() : v}>
                          <Input placeholder="Numéro CEDEAO" style={{ borderRadius: 8, textTransform: 'uppercase' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item name={['vehicule', 'puissance']} label="Puissance (CV)">
                          <InputNumber min={1} style={{ width: '100%', borderRadius: 8 }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item name={['vehicule', 'nbPlaces']} label="Nombre de places">
                          <InputNumber min={1} style={{ width: '100%', borderRadius: 8 }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={8}>
                        <Form.Item name="energie" label="Énergie">
                          <Select placeholder="Sélectionner" showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                            {refEnergies.map(r => <Option key={r._id} value={r.nom}>{r.nom}</Option>)}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item name="dateMiseCirculation" label="Date mise en circulation">
                          <MaskedDatePicker style={{ width: '100%' }} placeholder="JJ/MM/AAAA" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item name="valeurNeuf" label="Valeur neuf / venale (FCFA)">
                          <InputNumber {...FCFA_INPUT_PROPS} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={8}>
                        <Form.Item name="usageVehicule" label="Usage / Zone">
                          <Select placeholder="Sélectionner" showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                            {refUsagesVehicule.map(r => <Option key={r._id} value={r.nom}>{r.nom}</Option>)}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item name="tauxBns" label="Taux BNS (Bonus/Malus)">
                          <InputNumber min={0} max={2} step={0.01} style={{ width: '100%', borderRadius: 8 }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item name="conducteurHabituel" label="Conducteur habituel">
                          <Input placeholder="Ex: Salarie, Proprietaire..." style={{ borderRadius: 8 }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={8}>
                        <Form.Item name="classeConduteur" label="Classe conducteur">
                          <Select placeholder="Sélectionner" showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                            {refClassesConducteur.map(r => <Option key={r._id} value={r.nom}>{r.nom}</Option>)}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}
              </div>
            )}

            {/* Step 3: Prime calculation */}
            {currentStep === 2 && (
              <div>
                <Title level={5} style={{ marginBottom: 20 }}>
                  <DollarOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />
                  Calcul de la prime
                </Title>

                <Card style={{ borderRadius: 10, background: '#fafbfc', marginBottom: 20 }} bodyStyle={{ padding: isMobile ? 12 : 20 }}>
                  <Row gutter={16}>
                    <Col xs={24} sm={8}>
                      <Form.Item name="primeNette" label={<span>Prime nette (FCFA)<TermHelp term="primeNette" /></span>} rules={[{ required: true, message: 'La prime nette est obligatoire' }]}>
                        <InputNumber {...FCFA_INPUT_PROPS} onChange={calcPrime} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item name="tauxCommission" label={<span>Taux commission (%)<TermHelp term="tauxCommission" /></span>}>
                        <InputNumber min={0} max={100} style={{ width: '100%', borderRadius: 8 }} onChange={calcPrime} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="accessoiresCourtier" label={<span>Accessoires courtier (FCFA)<TermHelp term="accessoiresCourtier" /></span>} initialValue={0}>
                        <InputNumber {...FCFA_INPUT_PROPS} onChange={calcPrime} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="accessoiresAssureur" label={<span>Accessoires assureur (FCFA)<TermHelp term="accessoiresAssureur" /></span>} initialValue={0}>
                        <InputNumber {...FCFA_INPUT_PROPS} onChange={calcPrime} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>

                <Divider orientation="left" style={{ color: '#595959' }}>Decomposition</Divider>

                <Row gutter={16}>
                  <Col xs={12} sm={6}>
                    <Form.Item name="taxes" label={`Taxes${!taxesManuallyEdited ? ` (${(selectedType?.tauxTaxe != null && selectedType.tauxTaxe >= 0) ? selectedType.tauxTaxe : (settings.taxe_taux || 12)}%)` : ''}`}>
                      <InputNumber {...FCFA_INPUT_PROPS} onChange={handleTaxesChange} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item name="fga" label={<span>{`FGA${!fgaManuallyEdited ? ` (${settings.fga_taux || 2}%)` : ''}`}<TermHelp term="fga" /></span>}>
                      <InputNumber {...FCFA_INPUT_PROPS} onChange={handleFgaChange} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item name="cedeao" label={<span>CEDEAO<TermHelp term="cedeao" /></span>}>
                      <InputNumber {...FCFA_INPUT_PROPS} onChange={handleCedeaoChange} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item name="commission" label={<span>Commission courtier<TermHelp term="commission" /></span>}>
                      <InputNumber disabled style={{ width: '100%', borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Card style={{
                  background: 'linear-gradient(135deg, #1A2B5F 0%, #2d4a8a 100%)',
                  borderRadius: 12, textAlign: 'center', padding: '20px 0',
                }}>
                  <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14 }}>PRIME TTC</Text>
                  <div>
                    <Form.Item name="primeTTC" style={{ margin: 0 }}>
                      <InputNumber
                        disabled
                        style={{ width: 280, fontSize: 28, fontWeight: 700, background: 'transparent', border: 'none', color: '#fff', textAlign: 'center' }}
                        formatter={v => `${v} F`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                      />
                    </Form.Item>
                  </div>
                </Card>
              </div>
            )}

            {/* Step 4: Confirmation */}
            {currentStep === 3 && (() => {
              const compagnieNom = compagnies.find(c => c._id === formData.compagnie)?.nom || '';
              const typeNom = typesAssurance.find(t => t._id === formData.typeAssurance)?.nom || '';
              const agenceNom = agences.find(a => a._id === formData.agence)?.nom || '';
              const clientNom = selectedClient?.type === 'entreprise'
                ? (selectedClient?.raisonSociale || selectedClient?.nom || '')
                : `${selectedClient?.nom || ''} ${selectedClient?.prenom || ''}`.trim();
              const dateEffet = formData.dateEffet?.format ? formData.dateEffet.format('DD/MM/YYYY') : '';
              const dateEcheance = formData.dateEcheance?.format ? formData.dateEcheance.format('DD/MM/YYYY') : '';
              const dateProduction = formData.dateProduction?.format ? formData.dateProduction.format('DD/MM/YYYY') : '';

              return (
                <div>
                  <Title level={5} style={{ marginBottom: 20 }}>
                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                    Vérification et confirmation
                  </Title>

                  {/* Souscripteur */}
                  <Card size="small" style={{ borderRadius: 10, marginBottom: 16, borderLeft: '4px solid #1A2B5F' }}
                    title={<Text strong><UserOutlined style={{ marginRight: 6 }} />Souscripteur</Text>}
                    extra={<Button type="link" size="small" icon={<EditOutlined />} onClick={() => setCurrentStep(0)}>Modifier</Button>}>
                    <Row gutter={24}>
                      <Col xs={24} sm={8}><Text type="secondary">Nom</Text><br /><Text strong>{clientNom}</Text></Col>
                      <Col xs={24} sm={8}><Text type="secondary">Téléphone</Text><br /><Text strong>{formatPhone(selectedClient?.telephone)}</Text></Col>
                      <Col xs={24} sm={8}><Text type="secondary">Code</Text><br /><Text strong style={{ fontFamily: 'monospace' }}>{selectedClient?.code || ''}</Text></Col>
                    </Row>
                  </Card>

                  {/* Contrat */}
                  <Card size="small" style={{ borderRadius: 10, marginBottom: 16, borderLeft: '4px solid #1890ff' }}
                    title={<Text strong><FileProtectOutlined style={{ marginRight: 6 }} />Détails du contrat</Text>}
                    extra={<Button type="link" size="small" icon={<EditOutlined />} onClick={() => setCurrentStep(1)}>Modifier</Button>}>
                    <Row gutter={[24, 12]}>
                      <Col xs={12} sm={6}><Text type="secondary">Compagnie</Text><br /><Text strong>{compagnieNom}</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">Type</Text><br /><Tag color="blue">{typeNom}</Tag></Col>
                      <Col xs={12} sm={6}><Text type="secondary">Agence</Text><br /><Text strong>{agenceNom}</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">N° Police</Text><br /><Text strong style={{ fontFamily: 'monospace' }}>{formData.numeroPolice || ''}</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">N. Attestation</Text><br /><Text strong>{formData.numeroAttestation || '-'}</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">Duree</Text><br /><Text strong>{formData.duree || 12} mois</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">Date d'effet</Text><br /><Text strong>{dateEffet}</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">Date d'echeance</Text><br /><Text strong>{dateEcheance}</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">Date de production</Text><br /><Text strong>{dateProduction}</Text></Col>
                      {formData.immatriculation && <Col xs={12} sm={6}><Text type="secondary">Immatriculation</Text><br /><Text strong>{formData.immatriculation}</Text></Col>}
                      {formData.marqueVehicule && <Col xs={12} sm={6}><Text type="secondary">Véhicule</Text><br /><Text strong>{formData.marqueVehicule}</Text></Col>}
                      {formData.categorieVehicule && <Col xs={12} sm={6}><Text type="secondary">Catégorie vehicule</Text><br /><Text strong>{formData.categorieVehicule}</Text></Col>}
                    </Row>
                  </Card>

                  {/* Prime */}
                  <Card size="small" style={{ borderRadius: 10, marginBottom: 16, borderLeft: '4px solid #389e0d' }}
                    title={<Text strong><DollarOutlined style={{ marginRight: 6 }} />Calcul de la prime</Text>}
                    extra={<Button type="link" size="small" icon={<EditOutlined />} onClick={() => setCurrentStep(2)}>Modifier</Button>}>
                    <Row gutter={[24, 12]}>
                      <Col xs={12} sm={6}><Text type="secondary">Prime nette</Text><br /><Text strong>{fmt(formData.primeNette)} F</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">Accessoires courtier</Text><br /><Text strong>{fmt(formData.accessoiresCourtier)} F</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">Accessoires assureur</Text><br /><Text strong>{fmt(formData.accessoiresAssureur)} F</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">Taxes</Text><br /><Text strong>{fmt(formData.taxes)} F</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">FGA</Text><br /><Text strong>{fmt(formData.fga)} F</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">CEDEAO</Text><br /><Text strong>{fmt(formData.cedeao)} F</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">Commission</Text><br /><Text strong style={{ color: '#389e0d' }}>{fmt(formData.commission)} F</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">Revenu courtier</Text><br /><Text strong style={{ color: '#389e0d' }}>{fmt((formData.commission || 0) + (formData.accessoiresCourtier || 0))} F</Text></Col>
                      <Col xs={12} sm={6}><Text type="secondary">Reverse assureur</Text><br /><Text strong>{fmt(((formData.primeNette || 0) - (formData.commission || 0)) + (formData.accessoiresAssureur || 0))} F</Text></Col>
                    </Row>
                  </Card>

                  {/* Total */}
                  <Card style={{
                    background: 'linear-gradient(135deg, #1A2B5F 0%, #2d4a8a 100%)',
                    borderRadius: 12, textAlign: 'center',
                  }} bodyStyle={{ padding: '20px 0' }}>
                    <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, display: 'block' }}>PRIME TTC A PAYER</Text>
                    <Text style={{ color: '#fff', fontSize: 32, fontWeight: 700 }}>{fmt(formData.primeTTC)} F</Text>
                  </Card>
                </div>
              );
            })()}

            {/* Navigation */}
            <div className="nc-nav">
              <div>
                {currentStep > 0 && (
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => { setCurrentStep(prev => prev - 1); document.querySelector('.content-wrapper')?.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    size="large"
                    className="nc-btn-prev"
                  >
                    Précédent
                  </Button>
                )}
              </div>
              <div>
                {currentStep < 3 ? (
                  (() => {
                    const nextDisabled = currentStep === 0 && !selectedClient;
                    const nextTip = nextDisabled
                      ? 'Sélectionnez d\'abord un souscripteur pour continuer'
                      : '';
                    return (
                      <Tooltip title={nextTip}>
                        <Button
                          type="primary"
                          onClick={handleNext}
                          size="large"
                          disabled={nextDisabled}
                          className="nc-btn-next"
                        >
                          Suivant <ArrowRightOutlined />
                        </Button>
                      </Tooltip>
                    );
                  })()
                ) : (
                  <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={loading}
                    icon={<SaveOutlined />}
                    size="large"
                    className="nc-btn-submit"
                  >
                    {loading ? 'Enregistrement…' : (isEditMode ? 'Modifier le contrat' : 'Enregistrer le contrat')}
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default NouveauContrat;
